/* eslint-disable complexity */
import {
  Box,
  Button,
  Divider,
  Flex,
  Highlight,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  useDisclosure,
  useToken,
} from '@chakra-ui/react';
import { faClose, faCube, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  UseComboboxState,
  UseComboboxStateChangeOptions,
  useCombobox,
} from 'downshift';
import { Map as IMap } from 'immutable';
import { isEmpty, union } from 'lodash';
import React, { MutableRefObject, useCallback, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useViewerSearch } from '../../../hooks/model-search';
import { DbIdsPerModel, useViewer } from '../ForgeViewer';
import SparkelIcon from '../icon/SparkelIcon';

type SearchResultItem = {
  resolvedQueryString: string;
  dbIds: Map<string, Set<number>>;
  count: number;
  propertyCategory: string;
  propertyName: string;
  propertyValue: string;
};

const formatItemKey = (item: SearchResultItem) =>
  `${item.propertyCategory}-${item.propertyName}-${item.propertyValue}`;

const toDbIdsPerModel = (dbIds: Map<string, Set<number>>) => {
  const dbIdsPerModel: { [modelUrn: string]: number[] } = {};

  dbIds.forEach((dbIds, modelUrn) => {
    dbIdsPerModel[modelUrn] = Array.from(dbIds);
  });

  return dbIdsPerModel;
};

export function ElementSearchBar(): React.ReactElement {
  const [spacing2] = useToken('space', ['2']);
  const {
    onOpen: openSearchPopover,
    onClose: closeSearchPopover,
    isOpen: isSearchPopoverOpen,
  } = useDisclosure();

  const { search, searchResults, loading } = useViewerSearch();

  const items: SearchResultItem[] = Array.from(searchResults.values()).map(
    (result) => ({
      resolvedQueryString: result.resolvedQueryString,
      dbIds: result.dbIds,
      count: result.count,
      propertyCategory: result.propertyCategory,
      propertyName: result.propertyName,
      propertyValue: result.propertyValue,
    })
  );
  const { visibilityManager } = useViewer();

  const { t } = useTranslation('project');

  const parentRef = useRef<HTMLInputElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getItemKey: (index) => formatItemKey(items[index]),
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 2,
  });

  const [selectedItems, setSelectedItems] = useState<
    IMap<string, SearchResultItem>
  >(IMap());

  const downshiftStateReducer = useCallback(
    (
      state: UseComboboxState<SearchResultItem>,
      actionAndChanges: UseComboboxStateChangeOptions<SearchResultItem>
    ) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.FunctionSelectItem: {
          return {
            ...changes,
            isOpen: true,
            inputValue: state.inputValue,
          };
        }
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
            inputValue: state.inputValue,
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: true,
            inputValue: state.inputValue,
          };
        default:
          return changes;
      }
    },
    []
  );

  const {
    highlightedIndex,
    setHighlightedIndex,
    getInputProps,
    getMenuProps,
    getItemProps,
    inputValue,
    isOpen: isDropdownOpen,
  } = useCombobox<SearchResultItem>({
    items,
    onInputValueChange({ inputValue }) {
      if (typeof inputValue !== 'undefined') {
        search(inputValue);
      }
    },
    stateReducer: downshiftStateReducer,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    scrollIntoView: () => {},
    onSelectedItemChange({ selectedItem, type }) {
      if (
        selectedItem &&
        type !== useCombobox.stateChangeTypes.FunctionSelectItem
      ) {
        const itemKey = formatItemKey(selectedItem);
        setSelectedItems(IMap({ [itemKey]: selectedItem }));
        visibilityManager.isolate(toDbIdsPerModel(selectedItem.dbIds));
      }
    },
    onHighlightedIndexChange({ highlightedIndex, type }) {
      if (
        typeof highlightedIndex !== 'undefined' &&
        type !== useCombobox.stateChangeTypes.MenuMouseLeave
      ) {
        rowVirtualizer.scrollToIndex(highlightedIndex);
      }
    },
  });

  useHotkeys('/', openSearchPopover);
  useHotkeys(
    'tab',
    () => setHighlightedIndex((highlightedIndex + 1) % items.length),
    {
      enabled:
        !isEmpty(items) &&
        isSearchPopoverOpen &&
        typeof highlightedIndex !== 'undefined',
      enableOnFormTags: true,
      preventDefault: true,
    },
    [highlightedIndex, items.length, setHighlightedIndex]
  );

  useHotkeys(
    'shift+tab',
    () => {
      highlightedIndex === 0
        ? setHighlightedIndex(items.length - 1)
        : setHighlightedIndex(highlightedIndex - 1);
    },
    {
      enabled:
        !isEmpty(items) &&
        isSearchPopoverOpen &&
        typeof highlightedIndex !== 'undefined',
      enableOnFormTags: true,
      preventDefault: true,
    },
    [highlightedIndex, items.length, setHighlightedIndex]
  );

  const searchInputRef: MutableRefObject<HTMLInputElement | null> =
    useRef<HTMLInputElement>(null);

  const selectItem = (item: SearchResultItem, keepOthers: boolean) => {
    const itemKey = formatItemKey(item);
    let newItems = selectedItems;
    if (keepOthers) {
      newItems = newItems.set(itemKey, item);
    } else {
      newItems = IMap({ [itemKey]: item });
    }

    setSelectedItems(newItems);

    let dbIdsPerModel: DbIdsPerModel = {};
    for (const item of newItems.values()) {
      for (const [modelUrn, dbIds] of item.dbIds.entries()) {
        const existingDbIds: number[] = dbIdsPerModel[modelUrn] ?? [];
        dbIdsPerModel[modelUrn] = union(existingDbIds, Array.from(dbIds));
      }
    }
    visibilityManager.isolate(dbIdsPerModel);
  };

  const handleIsolateAll = () => {
    const allDbIdsPerModel: DbIdsPerModel = {};
    for (const item of items.values()) {
      for (const [modelUrn, dbIds] of item.dbIds.entries()) {
        const existingDbIds: number[] = allDbIdsPerModel[modelUrn] ?? [];
        allDbIdsPerModel[modelUrn] = union(existingDbIds, Array.from(dbIds));
      }
    }
    visibilityManager.isolate(allDbIdsPerModel);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <Popover
      initialFocusRef={searchInputRef}
      isOpen={isSearchPopoverOpen}
      onOpen={openSearchPopover}
      onClose={closeSearchPopover}
      placement="bottom-end"
    >
      <PopoverTrigger>
        <Box>
          <Tooltip
            label={t('element-search-button')}
            isDisabled={isSearchPopoverOpen}
          >
            <IconButton
              aria-label="Search for element"
              size="xs"
              colorScheme="gray"
              variant="solid-no-transparent"
              isRound
              icon={
                <SparkelIcon
                  icon={isSearchPopoverOpen ? faClose : faSearch}
                  color={['gray.600', 'gray.50']}
                />
              }
            />
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent width="4xl">
        <PopoverArrow />
        <Flex p={2}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SparkelIcon icon={faSearch} color={['gray.300', 'gray.500']} />
            </InputLeftElement>
            <Input
              flex={1}
              placeholder={t('element-search-placeholder')}
              {...getInputProps({
                ref: searchInputRef,
              })}
            />
          </InputGroup>
        </Flex>

        <Box
          {...getMenuProps({ ref: parentRef })}
          width="100%"
          position="relative"
          height="lg"
          overflowY="auto"
          display={isDropdownOpen ? 'block' : 'none'}
        >
          {items.length === 0 || inputValue.length === 0 ? (
            <Box
              textAlign="center"
              color="gray.400"
              _dark={{ color: 'gray.500' }}
            >
              {loading
                ? t('element-search.loading')
                : t('element-search.no-results')}
            </Box>
          ) : (
            <Box height={`${rowVirtualizer.getTotalSize()}px`}></Box>
          )}
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const index = virtualRow.index;
            const currentKey = virtualRow.key as string;
            const searchResult = items[index];

            const isSelected = selectedItems.has(currentKey);
            return (
              // eslint-disable-next-line react/jsx-key
              <Flex
                {...getItemProps({
                  item: searchResult,
                  index,
                  key: currentKey,
                  onClick: (event) => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // A small "escape hatch", as documented here: https://github.com/downshift-js/downshift/blob/3af206450d1da096d396e575d7b81d268cb7b12a/src/utils.js#L89
                    event.preventDownshiftDefault = true;
                    selectItem(searchResult, event.shiftKey);
                  },
                })}
                direction="row"
                alignItems="center"
                columnGap={2}
                cursor="pointer"
                fontWeight={isSelected ? 'bold' : 'normal'}
                color={highlightedIndex === index ? 'blue.700' : undefined}
                backgroundColor={
                  highlightedIndex === index ? 'blue.50' : undefined
                }
                _dark={{
                  backgroundColor:
                    highlightedIndex === index ? 'whiteAlpha.100' : '',
                  color: highlightedIndex === index ? 'blue.300' : '',
                }}
                position="absolute"
                top={0}
                left={2}
                right={2}
                height={`${virtualRow.size}px`}
                transform={`translateY(${virtualRow.start}px)`}
              >
                <SparkelIcon icon={faCube} style={{ marginRight: spacing2 }} />
                <Text as="span" color="gray.400" flex={1}>
                  {searchResult.propertyName}
                </Text>
                <Tooltip label={searchResult.propertyValue} openDelay={500}>
                  <Text as="span" noOfLines={1} flex={2} textAlign="left">
                    <Highlight
                      query={searchResult.resolvedQueryString}
                      styles={{
                        bgColor: 'orange.100',
                      }}
                    >
                      {searchResult.propertyValue}
                    </Highlight>
                  </Text>
                </Tooltip>
                <Text as="span" color="gray.400" textAlign="right" width="5em">
                  ({searchResult.count})
                </Text>
                {/* <Text
                    as="span"
                    color="gray.300"
                    _dark={{ color: 'gray.500' }}
                  >
                    ({searchResult.dbIds.size})
                  </Text> */}

                <Text
                  as="span"
                  flex={1}
                  noOfLines={1}
                  color="gray.300"
                  textAlign="right"
                  _dark={{ color: 'gray.500' }}
                >
                  {searchResult.propertyCategory}
                </Text>
              </Flex>
            );
          })}
        </Box>
        <Box display={isDropdownOpen && items.length > 0 ? 'block' : 'none'}>
          <Divider />
          <Button
            variant="ghost"
            borderRadius={0}
            width="100%"
            colorScheme="gray"
            color="gray.400"
            onClick={handleIsolateAll}
          >
            {items.length} results
          </Button>
        </Box>
      </PopoverContent>
    </Popover>
  );
}
